import { adminFetch } from '@/lib/apiClient';
import type {
  ConfirmedItem,
  MediaCategory,
  PresignFileRequest,
  PresignedItem,
} from './types';

const PRESIGN_PATH = '/admin/v1/media/presign';
const CONFIRM_PATH = '/admin/v1/media/confirm';

export interface PresignInput {
  file: File;
  category: MediaCategory;
}

/**
 * POST /admin/v1/media/presign — 배치 presign.
 * 입력 순서를 그대로 보존한 PresignedItem[] 을 반환한다.
 */
export const presign = async (
  inputs: ReadonlyArray<PresignInput>,
): Promise<PresignedItem[]> => {
  const files: PresignFileRequest[] = inputs.map(({ file, category }) => ({
    filename: file.name,
    contentType: file.type,
    sizeBytes: file.size,
    category,
  }));

  const res = await adminFetch(PRESIGN_PATH, {
    method: 'POST',
    body: JSON.stringify({ files }),
  });
  if (!res.ok) {
    throw new Error(`업로드 URL 발급에 실패했습니다. (${res.status})`);
  }
  const env = (await res.json()) as { data?: { items?: PresignedItem[] } };
  const items = env.data?.items ?? [];
  if (items.length !== inputs.length) {
    throw new Error('업로드 URL 발급 응답이 올바르지 않습니다.');
  }
  return items;
};

/**
 * presigned URL 로 S3 직접 PUT. apiClient 가 아닌 raw fetch 를 사용한다
 * (S3 는 게이트웨이/인증 헤더 대상이 아니며, requiredHeaders 만 전송).
 */
const putToS3 = async (item: PresignedItem, file: File): Promise<void> => {
  const res = await fetch(item.uploadUrl, {
    method: item.uploadMethod,
    headers: item.requiredHeaders,
    body: file,
  });
  if (!res.ok) {
    throw new Error(`이미지 업로드에 실패했습니다. (${res.status})`);
  }
};

/** 모든 파일을 병렬로 S3 에 직접 업로드한다. 하나라도 실패하면 reject. */
export const uploadToS3 = async (
  pairs: ReadonlyArray<{ item: PresignedItem; file: File }>,
): Promise<void> => {
  await Promise.all(pairs.map(({ item, file }) => putToS3(item, file)));
};

/**
 * POST /admin/v1/media/confirm — 업로드 확정 후 publicUrl 회수.
 * fileId → publicUrl 매핑을 반환한다.
 */
export const confirm = async (
  fileIds: ReadonlyArray<string>,
): Promise<Map<string, string>> => {
  if (fileIds.length === 0) return new Map();

  const res = await adminFetch(CONFIRM_PATH, {
    method: 'POST',
    body: JSON.stringify({ fileIds: [...fileIds] }),
  });
  if (!res.ok) {
    throw new Error(`업로드 확정에 실패했습니다. (${res.status})`);
  }
  const env = (await res.json()) as { data?: { items?: ConfirmedItem[] } };
  const items = env.data?.items ?? [];
  return new Map(items.map((it) => [it.fileId, it.publicUrl]));
};
